/**
 * Post-authentication step shared by the login and register routes: uploads the
 * guest's pending writes to the new account, then registers the account in the
 * multi-account registry and makes it this tab's account.
 */
export interface PersistAuthenticatedAccountInput<TAccount extends { userId: string }, TData> {
  data: TData;
  /** The real account id from the auth response, or null when there is none. */
  userId: string | null;
  /** True when the user is adding an account next to the current one (no guest upload). */
  isAdding: boolean;
  /** Persists the marker that a page load during the upload resumes it. */
  markGuestUpgrade: (userId: string) => void;
  flushGuestUpgrade: (userId: string) => Promise<void>;
  registerAccount: (data: TData) => Promise<TAccount | null>;
  activateTab: (userId: string) => void;
}

export async function persistAuthenticatedAccount<TAccount extends { userId: string }, TData>(
  input: PersistAuthenticatedAccountInput<TAccount, TData>,
): Promise<TAccount | null> {
  // Adding an account keeps the current one, so there is no guest upload to resume.
  const guestUpgradeUserId = input.isAdding ? null : input.userId;
  // Mark before anything else: a page load from here on must find the pending
  // upload, even if it lands before the account is registered.
  if (guestUpgradeUserId) {
    input.markGuestUpgrade(guestUpgradeUserId);
  }
  // Register before the upload: the upload is slow, and a page load during it
  // must still find the account in the registry and in this tab.
  const account = await input.registerAccount(input.data);
  if (account) {
    input.activateTab(account.userId);
  }
  if (guestUpgradeUserId) {
    await input.flushGuestUpgrade(guestUpgradeUserId);
  }
  return account;
}
